import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";

export interface WorkspaceProject {
  id: string;
  workspace_id: string;
  name: string;
  slug: string;
  description: string | null;
  color: string;
  is_archived: boolean;
  is_default: boolean;
  order_index: number;
  created_at: string;
  updated_at: string;
}

export interface WorkspaceProjectColumn {
  id: string;
  project_id: string;
  workspace_id: string;
  name: string;
  slug: string;
  color: string;
  order_index: number;
  is_system: boolean;
  created_at: string;
  updated_at: string;
}

function slugifyName(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export function useWorkspaceProjects(workspaceId: string | null) {
  const [projects, setProjects] = useState<WorkspaceProject[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchProjects = useCallback(async () => {
    if (!workspaceId) {
      setProjects([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("workspace_projects")
        .select("*")
        .eq("workspace_id", workspaceId)
        .eq("is_archived", false)
        .order("order_index", { ascending: true });

      if (error) throw error;
      setProjects((data ?? []) as WorkspaceProject[]);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Erro ao carregar projetos",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [workspaceId, toast]);

  const fetchProjectsRef = useRef(fetchProjects);
  fetchProjectsRef.current = fetchProjects;

  useEffect(() => {
    void fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    if (!workspaceId) return;

    const channelName = `workspace-projects-${workspaceId}`;
    const channel = subscribePostgresChannel(
      channelName,
      {
        event: "*",
        schema: "public",
        table: "workspace_projects",
        filter: `workspace_id=eq.${workspaceId}`,
      },
      () => {
        void fetchProjectsRef.current();
      },
    );

    return () => {
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
    };
  }, [workspaceId]);

  const createProject = async (input: {
    name: string;
    description?: string | null;
    color?: string;
  }): Promise<WorkspaceProject | null> => {
    if (!workspaceId) return null;

    try {
      const slug = slugifyName(input.name);
      const maxOrder = projects.reduce((max, p) => Math.max(max, p.order_index), -1);

      const { data, error } = await supabase
        .from("workspace_projects")
        .insert({
          workspace_id: workspaceId,
          name: input.name.trim(),
          slug,
          description: input.description?.trim() || null,
          color: input.color ?? "bg-blue-500",
          order_index: maxOrder + 1,
        })
        .select()
        .single();

      if (error) throw error;

      await fetchProjects();
      toast({
        title: "Projeto criado",
        description: `O projeto "${input.name}" foi criado.`,
      });
      return data as WorkspaceProject;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Erro ao criar projeto",
        description: message,
        variant: "destructive",
      });
      return null;
    }
  };

  const updateProject = async (
    id: string,
    patch: Partial<Pick<WorkspaceProject, "name" | "description" | "color" | "order_index">>,
  ): Promise<boolean> => {
    try {
      const update: Record<string, unknown> = { ...patch };
      if (patch.name) update.slug = slugifyName(patch.name);

      const { error } = await supabase
        .from("workspace_projects")
        .update(update)
        .eq("id", id);

      if (error) throw error;
      await fetchProjects();
      toast({ title: "Projeto atualizado" });
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Erro ao atualizar projeto",
        description: message,
        variant: "destructive",
      });
      return false;
    }
  };

  const archiveProject = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("workspace_projects")
        .update({ is_archived: true })
        .eq("id", id);

      if (error) throw error;
      await fetchProjects();
      toast({ title: "Projeto arquivado" });
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Erro ao arquivar projeto",
        description: message,
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    projects,
    loading,
    refetch: fetchProjects,
    createProject,
    updateProject,
    archiveProject,
  };
}

export function useProjectColumns(projectId: string | null) {
  const [columns, setColumns] = useState<WorkspaceProjectColumn[]>([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const fetchColumns = useCallback(async () => {
    if (!projectId) {
      setColumns([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("workspace_project_columns")
        .select("*")
        .eq("project_id", projectId)
        .order("order_index", { ascending: true });

      if (error) throw error;
      setColumns((data ?? []) as WorkspaceProjectColumn[]);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Erro ao carregar colunas",
        description: message,
        variant: "destructive",
      });
      setColumns([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, toast]);

  const fetchColumnsRef = useRef(fetchColumns);
  fetchColumnsRef.current = fetchColumns;

  useEffect(() => {
    if (!projectId) {
      setColumns([]);
      setLoading(false);
      return;
    }

    // Evita colunas stale de outro projeto durante o carregamento.
    setColumns([]);
    setLoading(true);
    void fetchColumns();
  }, [projectId, fetchColumns]);

  useEffect(() => {
    if (!projectId) return;

    const channelName = `workspace-project-columns-${projectId}`;
    const channel = subscribePostgresChannel(
      channelName,
      {
        event: "*",
        schema: "public",
        table: "workspace_project_columns",
        filter: `project_id=eq.${projectId}`,
      },
      () => {
        void fetchColumnsRef.current();
      },
    );

    return () => {
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
    };
  }, [projectId]);

  const createColumn = async (input: {
    name: string;
    color: string;
    workspaceId: string;
  }): Promise<WorkspaceProjectColumn | null> => {
    if (!projectId) return null;

    try {
      const slug = slugifyName(input.name);
      const maxOrder = columns.reduce((max, c) => Math.max(max, c.order_index), -1);

      const { data, error } = await supabase
        .from("workspace_project_columns")
        .insert({
          project_id: projectId,
          workspace_id: input.workspaceId,
          name: input.name.trim(),
          slug,
          color: input.color,
          order_index: maxOrder + 1,
        })
        .select()
        .single();

      if (error) throw error;
      await fetchColumns();
      toast({
        title: "Coluna criada",
        description: `A coluna "${input.name}" foi adicionada.`,
      });
      return data as WorkspaceProjectColumn;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Erro ao criar coluna",
        description: message,
        variant: "destructive",
      });
      return null;
    }
  };

  const updateColumn = async (
    id: string,
    patch: Partial<Pick<WorkspaceProjectColumn, "name" | "color" | "order_index">>,
  ): Promise<boolean> => {
    try {
      const update: Record<string, unknown> = { ...patch };
      if (patch.name) update.slug = slugifyName(patch.name);

      const { error } = await supabase
        .from("workspace_project_columns")
        .update(update)
        .eq("id", id);

      if (error) throw error;
      await fetchColumns();
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Erro ao atualizar coluna",
        description: message,
        variant: "destructive",
      });
      return false;
    }
  };

  const deleteColumn = async (id: string): Promise<boolean> => {
    try {
      const { error, count } = await supabase
        .from("workspace_project_columns")
        .delete({ count: "exact" })
        .eq("id", id);

      if (error) throw error;

      if (!count) {
        toast({
          title: "Não foi possível excluir",
          description: "Coluna de sistema ou sem permissão.",
          variant: "destructive",
        });
        return false;
      }

      await fetchColumns();
      toast({ title: "Coluna excluída" });
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      toast({
        title: "Erro ao excluir coluna",
        description: message,
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    columns,
    loading,
    refetch: fetchColumns,
    createColumn,
    updateColumn,
    deleteColumn,
  };
}
